import { useCallback, useMemo } from 'react'

import * as SliderPrimitive from '@radix-ui/react-slider'

import { NumberFormat } from 'src/utils/number'

import HStack from '../Flex/HStack'
import FormControl from '../FormControl/FormControl'
import { FormControlProps } from '../FormControl/util'
import NumberInput from '../Input/NumberInput'

type Props = FormControlProps & {
  onValueChange: (value: number) => void
  value: number
}
const Slider = ({ onValueChange, value, width = '100%', ...formControlProps }: Props) => {
  const arrayValue = useMemo(() => [value], [value])
  const handleValueChange = useCallback(
    (newValue: number[] | number | undefined) => {
      if (!newValue) {
        onValueChange(0)
      } else if (Array.isArray(newValue)) {
        onValueChange(newValue[0] ?? 0)
      } else {
        onValueChange(newValue)
      }
    },
    [onValueChange]
  )
  return (
    <FormControl {...formControlProps} width={width}>
      {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
        <HStack gap={2}>
          <SliderPrimitive.Root
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-live={ariaLive}
            className="relative flex w-full touch-none items-center pt-1 select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col"
            data-slot="slider"
            id={formControlProps.id}
            max={100}
            min={0}
            onValueChange={handleValueChange}
            step={1}
            value={arrayValue}
          >
            <SliderPrimitive.Track
              data-slot="slider-track"
              className="relative grow overflow-hidden rounded-full bg-neutral-200 data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5"
            >
              <SliderPrimitive.Range
                data-slot="slider-range"
                className="absolute bg-sky-500 data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full"
              />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb
              data-slot="slider-thumb"
              className="ring-ring/50 block size-4 shrink-0 cursor-grab rounded-full border border-sky-500 bg-white shadow-sm ring-sky-200 transition-[color,box-shadow] hover:ring-2 focus-visible:ring-2 focus-visible:outline-hidden active:cursor-grabbing disabled:pointer-events-none disabled:opacity-50"
            />
          </SliderPrimitive.Root>
          <NumberInput
            value={value}
            onValueChange={handleValueChange}
            ariaLabel="Slider value"
            format={NumberFormat.Integer}
            max={100}
            min={0}
            width="96px"
            textAlign="right"
            leftAddon={null}
            suffix="%"
          />
        </HStack>
      )}
    </FormControl>
  )
}

export default React.memo(Slider)
